import React, { useState, useEffect } from "react";
import { NameCard } from "./NameCard";
import { teamsService } from "../../../apis/Business/ActionPlanTeamApi";
import { getBusinessList } from "../../../apis/Business/Business";
import accessService from "../../../apis/Profile/AccessServiceApi";
import plusIcon from "../../../assets/shapes_svg/ambition/plus-icon.svg";
import BatchInviteModal from "../../NewProfile/AccessManager/BatchInviteModal";
import { useUserAccess } from "../../../components/WriteAccessContext";
// const DEPARTMENTS = ["Engineering", "Design", "Marketing", "Sales"];

const AddPersonDialog = ({ onAdd, onClose, facilities }) => {
  const [formData, setFormData] = useState({
    facility: "", // This will store the facility ID
    // department: "Engineering",
    shared_user: "", // Changed from colleague to shared_user
  });

  const [sharedUsers, setSharedUsers] = useState([]); // Shared users from WriteAccess
  const [showInviteForm, setShowInviteForm] = useState(false);

  // Fetch shared users when component mounts
  useEffect(() => {
    const fetchSharedUsers = async () => {
      try {
        // Get shared users from WriteAccess
        const data = await accessService.getSharedUserAccess();
        setSharedUsers(data);
      } catch (err) {
        console.error("Error fetching shared users:", err);
        setSharedUsers([]);
      }
    };

    fetchSharedUsers();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
    onClose();
  };
  return (
    <div className="dialog-overlay">
      <div
        className="dialog"
        style={{ maxHeight: "70vh", overflowY: "scroll" }}
      >
        <h3>Add New Entry</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="facility">Facility:</label>
            <select
              id="facility"
              value={formData.facility}
              onChange={(e) =>
                setFormData({ ...formData, facility: e.target.value })
              }
              className="transparent-select"
            >
              <option value="">Select a facility</option>
              {facilities.map((fac) => (
                <option key={fac.id} value={fac.id}>
                  {fac.name}
                </option>
              ))}
            </select>
          </div>
          {/* <div className="form-group">
            <label htmlFor="department">Department:</label>
            <select
              id="department"
              value={formData.department}
              onChange={(e) =>
                setFormData({ ...formData, department: e.target.value })
              }
              className="transparent-select"
            >
              {DEPARTMENTS.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div> */}
          {sharedUsers.length > 0 ? (
            <div className="form-group">
              <label htmlFor="shared_user">Team Member:</label>
              <select
                id="shared_user"
                value={formData.shared_user}
                onChange={(e) =>
                  setFormData({ ...formData, shared_user: e.target.value })
                }
                className="transparent-select"
              >
                <option value="">Select a team member</option>
                {sharedUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name || `${user.first_name} ${user.last_name}`}
                  </option>
                ))}
              </select>
            </div>
          ) : !showInviteForm ? (
            <div className="form-group">
              <label>You don't have team members yet.</label>
              <button
                type="button"
                onClick={() => setShowInviteForm(true)}
                className="add-colleague-btn"
              >
                Invite Team Member
              </button>
            </div>
          ) : null}

          {showInviteForm && (
            <BatchInviteModal
              isOpen={showInviteForm}
              onClose={() => setShowInviteForm(false)}
            />
          )}

          <div className="dialog-buttons">
            <button type="submit" className="submit-button">
              Add
            </button>
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TeamSection = ({ effortId, year }) => {
  const [facilities, setFacilities] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [teams, setTeams] = useState([]);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userAccess } = useUserAccess();

  useEffect(() => {
    getBusinessList({ setFacilities: setFacilities, filterYear: year });
  }, [year]);

  // Fetch shared users from WriteAccess
  useEffect(() => {
    const fetchSharedUsers = async () => {
      try {
        const data = await accessService.getSharedUserAccess();
        setSharedUsers(data);
      } catch (err) {
        console.error("Error fetching shared users:", err);
        setSharedUsers([]);
      }
    };

    fetchSharedUsers();
  }, []);

  useEffect(() => {
    const fetchTeams = async () => {
      if (!facilities.length) return;
      if (!effortId) return;

      try {
        setLoading(true);
        const teamsData = await teamsService.getTeams(effortId);

        // Process team data with shared user details
        const teamsWithDetails = teamsData.map((team) => {
          const sharedUser = sharedUsers.find(
            (user) => user.id === team.shared_user
          );
          const facility = facilities.find((fac) => fac.id === team.facility);

          return {
            ...team,
            facilityName: facility?.name || "Unknown Facility",
            sharedUserName: sharedUser
              ? sharedUser.name ||
                `${sharedUser.first_name} ${sharedUser.last_name}`
              : "Unknown User",
          };
        });

        setTeams(teamsWithDetails);
      } catch (error) {
        console.error("Failed to fetch teams:", error);
      } finally {
        setLoading(false);
      }
    };

    if (sharedUsers.length > 0) {
      fetchTeams();
    }
  }, [effortId, facilities, sharedUsers]);

  const handleAdd = async (newData) => {
    try {
      const createdTeam = await teamsService.createTeam(effortId, newData);

      // Find the shared user and facility details
      const sharedUser = sharedUsers.find(
        (user) => user.id === createdTeam.shared_user
      );
      const facility = facilities.find((f) => f.id === createdTeam.facility);

      // Add details to the new team
      const newTeamWithDetails = {
        ...createdTeam,
        facilityName: facility?.name || "Unknown Facility",
        sharedUserName: sharedUser
          ? sharedUser.name ||
            `${sharedUser.first_name} ${sharedUser.last_name}`
          : "Unknown User",
      };

      setTeams([...teams, newTeamWithDetails]);
      setShowAddDialog(false);
    } catch (error) {
      console.error("Failed to create team:", error);
    }
  };

  const handleUpdate = async (teamId, updatedData) => {
    try {
      const updatedTeam = await teamsService.updateTeam(
        effortId,
        teamId,
        updatedData
      );

      // Find the shared user and facility details
      const sharedUser = sharedUsers.find(
        (user) => user.id === updatedTeam.shared_user
      );
      const facility = facilities.find((f) => f.id === updatedTeam.facility);

      const updatedTeamWithDetails = {
        ...updatedTeam,
        facilityName: facility?.name || "Unknown Facility",
        sharedUserName: sharedUser
          ? sharedUser.name ||
            `${sharedUser.first_name} ${sharedUser.last_name}`
          : "Unknown User",
      };

      setTeams(
        teams.map((team) =>
          team.id === teamId ? updatedTeamWithDetails : team
        )
      );
    } catch (error) {
      console.error("Failed to update team:", error);
    }
  };

  return (
    <div className="team-section">
      <NameCard
        deptName={"Team"}
        isBottom={false}
        className={"other-effort-container"}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
        className="action-plans-parent-bubble"
      >
        {teams && teams.length > 0 && (
          <div className="team-grid">
            {teams.map((team) => (
              <div key={team.id} className="team-columns">
                <div className="team-column">
                  <button className="team-button">
                    <select
                      value={team.facility}
                      disabled={userAccess !== "write"}
                      onChange={(e) => {
                        if (userAccess === "write") {
                          handleUpdate(team.id, {
                            ...team,
                            facility: e.target.value,
                          });
                        }
                      }}
                      className="select-clean"
                    >
                      {facilities.map((facility) => (
                        <option key={facility.id} value={facility.id}>
                          {facility.name}
                        </option>
                      ))}
                    </select>
                  </button>
                </div>
                {/* <div className="team-column">
                  <button className="team-button">
                    <select
                      value={team.department}
                      onChange={(e) =>
                        handleUpdate(team.id, {
                          ...team,
                          department: e.target.value,
                        })
                      }
                      className="select-clean"
                    >
                      {DEPARTMENTS.map((dept) => (
                        <option key={dept} value={dept}>
                          {dept}
                        </option>
                      ))}
                    </select>
                  </button>
                </div> */}
                <div className="team-column">
                  <button className="team-button">
                    <select
                      value={team.shared_user}
                      disabled={userAccess !== "write"}
                      onChange={(e) => {
                        if (userAccess === "write") {
                          handleUpdate(team.id, {
                            ...team,
                            shared_user: e.target.value,
                          });
                        }
                      }}
                      className="select-clean"
                    >
                      <option value="">Select a team member</option>
                      {sharedUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name || `${user.first_name} ${user.last_name}`}
                        </option>
                      ))}
                    </select>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <button
          style={{
            border: "none",
            background: "transparent",
            display: "flex",
            alignItems: "center",
            gap: "15.72px",
          }}
          disabled={userAccess !== "write"}
          onClick={() => setShowAddDialog(true)}
        >
          <img src={plusIcon} alt="Plus icon" width={37} height={37} />
          <h5 style={{ color: "#1B3764", fontSize: "14px" }}>Add Person</h5>
        </button>
      </div>

      {showAddDialog && (
        <AddPersonDialog
          onAdd={handleAdd}
          onClose={() => setShowAddDialog(false)}
          facilities={facilities}
        />
      )}
    </div>
  );
};

export default TeamSection;
